import type { WebWidgetModel } from '@web/types';
import {
    EButtonStyleType,
    EButtonWidthType,
    EFontStyle,
    EHorizontalPosition,
    ELineStyle,
    ESize,
    InputBorderEnum,
    Widgets,
} from '@common/enums';
import { defaultWidgetParams, defaultWidgetPermission } from '@common/constants';
import { type JssStyle } from 'jss';
import { type TranslateService } from '@ngx-translate/core';
import {
    DEFAULT_ITEMS_IN_ROW_DESKTOP,
    DEFAULT_ITEMS_IN_ROW_MOBILE,
    MAX_PER_PAGE,
} from '@web/widgets/catalog-products/constants/defaults-catalog-products.const';
import {
    AspectRatio,
    Attachment,
    CatalogCategoriesView,
    type CatalogProductsWidgetData,
} from '@web/widgets/catalog-products/type';
import { ButtonActionType } from '@web/widgets/product-card/type';
import {
    DEFAULT_BUTTON_ADDITIONAL_EFFECTS,
    DEFAULT_BUTTON_BASE,
    DEFAULT_BUTTON_HOVER_STATES,
    DEFAULT_BUTTON_ICON,
} from '@web/widgets/common/base/base-button/base-button.const';
import { TRANSPARENT_VALUE } from '@components/sp-color-picker/src/constants/color-picker-presets.const';
import { buttonIconTemplates } from '@common/icons/button-icons';
import {
    CatalogProductsControlComponent,
    CatalogProductsViewComponent,
} from '@web/widgets/catalog-products/components';

const catalogProductsData = (translateService: TranslateService): CatalogProductsWidgetData => ({
    selectedCategory: null,
    catalogView: CatalogCategoriesView.left,
    itemsInRow: {
        desktop: DEFAULT_ITEMS_IN_ROW_DESKTOP,
        mobile: DEFAULT_ITEMS_IN_ROW_MOBILE,
    },
    maxPerPage: MAX_PER_PAGE,
    basketButton: {
        label: translateService.instant('widget_product_basket_action_add'),
        styleType: EButtonStyleType.primary,
        size: ESize.M,
        action: ButtonActionType.basket,
        buttonBase: { ...DEFAULT_BUTTON_BASE },
        buttonHover: { ...DEFAULT_BUTTON_HOVER_STATES },
        isShowButton: true,
        buttonIcon: {
            isActive: false,
            icon: buttonIconTemplates.arrowRight1,
            position: EHorizontalPosition.L,
        },
    },
    pagination: {
        pagesButtons: {
            textColor: '#333',
            backgroundColor: '#fff',
            borderColor: '#009FEF',
            borderRadius: 4,
            inputSize: ESize.M,
        },
        currentPage: {
            textColor: '#333',
            backgroundColor: '#DAF3FF',
            borderColor: '#009FEF',
        },
        nextPage: {
            label: translateService.instant('catalog_pagination_next_page_title'),
            styleType: EButtonStyleType.primary,
            size: ESize.M,
            buttonBase: { ...DEFAULT_BUTTON_BASE },
            buttonHover: { ...DEFAULT_BUTTON_HOVER_STATES },
            widthType: EButtonWidthType.full,
            horizontalFormWidthType: EButtonWidthType.auto,
            widthValue: 260,
            buttonIcon: DEFAULT_BUTTON_ICON,
            additionalEffects: DEFAULT_BUTTON_ADDITIONAL_EFFECTS,
            isShowButton: false,
        },
    },
    filter: {
        general: {
            backgroundColor: TRANSPARENT_VALUE,
            borderRadius: 0,
            isBorder: null,
            borderWidth: 1,
            borderStyle: ELineStyle.SOLID,
            borderColor: '#000',
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 0,
            paddingRight: 0,
        },
        category: {
            label: translateService.instant('catalog_filter_default_label'),
            categoryLabelFont: {
                color: '#333',
                fontSize: 16,
                fontStyle: [EFontStyle.B],
            },
            categoryFont: {
                color: '#333',
                fontSize: 16,
                fontStyle: [],
            },
            selectedCategoryFont: {
                color: '#1cb2f5',
                fontSize: 16,
                fontStyle: [],
            },
            selectFilterSettings: {
                borderStyle: InputBorderEnum.FULL_BORDER,
                backgroundColor: '#fff',
                textColor: '#333',
                borderColor: '#ddd',
                borderRadius: 3,
                inputSize: ESize.M,
            },
        },
        price: {
            label: translateService.instant('catalog_price_label'),
            priceLabelFont: {
                color: '#333',
                fontSize: 16,
                fontStyle: [EFontStyle.B],
            },
            borderStyle: InputBorderEnum.FULL_BORDER,
            textColor: '#333',
            backgroundColor: '#fff',
            borderColor: '#ddd',
            borderRadius: 3,
            inputSize: ESize.M,
            slider: true,
            colorLine: '#fff',
            colorFilledLine: '#1cb2f5',
            colorButtons: '#ddd',
        },
    },
    sorting: {
        enable: true,
        borderStyle: InputBorderEnum.FULL_BORDER,
        backgroundColor: '#fff',
        textColor: '#333',
        borderColor: '#ddd',
        borderRadius: 3,
        inputSize: ESize.M,
        searchEnable: true,
        searchPlaceholder: translateService.instant('catalog_search_placeholder'),
    },
    settings: {
        general: {
            margin: 30,
        },
        gallery: {
            aspectRatio: AspectRatio.onoToOne,
            attachment: Attachment.contain,
            backgroundColor: '#eee',
            borderRadius: 0,
            margin: 25,
        },
        typography: {
            titleSettings: {
                color: '#333',
                fontSize: 18,
                fontStyle: [EFontStyle.B],
            },
            counterSettings: {
                color: '#333',
                fontSize: 14,
                fontStyle: [],
            },
            priceSettings: {
                color: '#333',
                fontSize: 16,
                fontStyle: [],
                isShow: true,
            },
            oldPriceSettings: {
                color: '#c9c9d9',
                fontSize: 16,
                fontStyle: [],
                isShow: true,
            },
        },
        card: {
            backgroundColor: TRANSPARENT_VALUE,
            borderRadius: 0,
            isBorder: null,
            padding: 0,
            borderWidth: 1,
            borderStyle: ELineStyle.SOLID,
            borderColor: '#000',
        },
    },
    userHash: null,
    categories: null,
    products: null,
    total: null,
});

const catalogProductsStyles: JssStyle = {
    maxWidth: '100%',
    margin: '0 auto',
    display: 'block',
};
export const catalogProductsWidgetModel: WebWidgetModel = {
    componentView: CatalogProductsViewComponent,
    componentControl: CatalogProductsControlComponent,
    name: Widgets.CATALOG_PRODUCTS,
    label: 'widget_catalog_products_label',
    data: catalogProductsData,
    styles: catalogProductsStyles,
    permission: defaultWidgetPermission,
    params: defaultWidgetParams,
};
